import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { Form } from 'react-bootstrap'
import { notify } from '../component-items/helpers'
import { globalState } from '../../store'
import { UserType } from './RoleBadge'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faTimes,
  faCheckCircle,
  faTimesCircle,
  faBug,
  faInfoCircle,
  faUserShield,
  faShieldAlt,
} from '@fortawesome/pro-duotone-svg-icons'

interface PermissionDebuggerTabProps {
  userType: UserType
  warehouseId?: number
  companyId?: number
}

interface UserOption {
  id: number
  name: string
  email?: string
}

interface PermissionCheckResult {
  allowed: boolean
  reason: string
  user_type: string
  user_role: string
  segment: string
  requested_level: string
  effective_level: string
  has_override: boolean
}

// Styled components
const DebuggerContainer = styled.div`
  padding: 1.5rem;
  max-width: 800px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  svg {
    font-size: 1.5rem;
    color: #6f42c1;
  }

  h4 {
    margin: 0;
    font-weight: 500;
  }

  p {
    margin: 0;
    font-size: 0.9em;
    color: #6c757d;
  }
`

const FormCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: 500;
    font-size: 0.9em;
  }
`

const SearchableSelect = styled.div`
  position: relative;
`

const SearchInput = styled.div`
  position: relative;

  input {
    width: 100%;
    height: 38px;
    padding: 0.375rem 0.75rem 0.375rem 2rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: #86b7fe;
      box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
    }
  }

  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
  }
`

const DropdownList = styled.ul<{ $show: boolean }>`
  display: ${(props) => (props.$show ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin: 0.25rem 0 0 0;
  padding: 0;
  list-style: none;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  li {
    padding: 0.5rem 0.75rem;
    cursor: pointer;

    &:hover {
      background: #f8f9fa;
    }

    &.no-results {
      color: #6c757d;
      font-style: italic;
      cursor: default;

      &:hover {
        background: white;
      }
    }

    small {
      color: #6c757d;
    }
  }
`

const SelectedValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 38px;
  padding: 0.375rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #e7f1ff;
  cursor: pointer;

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;

    &:hover {
      color: #dc3545;
    }
  }
`

const CheckButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: #6f42c1;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #5a32a3;
  }

  &:disabled {
    background: #adb5bd;
    cursor: not-allowed;
  }
`

const ResultCard = styled.div<{ $allowed: boolean }>`
  border: 2px solid ${(props) => (props.$allowed ? '#198754' : '#dc3545')};
  border-radius: 8px;
  overflow: hidden;
`

const ResultHeader = styled.div<{ $allowed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: ${(props) => (props.$allowed ? '#d1e7dd' : '#f8d7da')};
  color: ${(props) => (props.$allowed ? '#0f5132' : '#842029')};

  svg {
    font-size: 1.5rem;
  }

  h5 {
    margin: 0;
    font-weight: 600;
  }
`

const ResultBody = styled.div`
  padding: 1.5rem;
  background: white;
`

const ReasonBox = styled.div`
  background: #f8f9fa;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.95em;
  line-height: 1.5;
`

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  label {
    font-size: 0.8em;
    color: #6c757d;
    font-weight: 500;
    text-transform: uppercase;
  }

  span {
    font-size: 0.95em;
  }
`

const OverrideBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2em 0.5em;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 500;
  background: #fff3cd;
  color: #856404;
`

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: #cff4fc;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 0.9em;
  color: #055160;

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`

// Segment options (matching backend BusinessSegment enum)
const SEGMENT_OPTIONS = [
  { value: 'outbound', label: 'Outbound' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'products', label: 'Products' },
  { value: 'vas', label: 'VAS' },
  { value: 'billing', label: 'Billing' },
  { value: 'postage', label: 'Postage' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'settings', label: 'Settings' },
  { value: 'reports', label: 'Reports' },
]

// Access level options
const LEVEL_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'operator', label: 'Operator' },
  { value: 'admin', label: 'Admin' },
]

export const PermissionDebuggerTab = ({ userType, warehouseId, companyId }: PermissionDebuggerTabProps) => {
  const {
    state: { csrf },
  } = globalState()

  // Form state
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [userSearch, setUserSearch] = useState('')
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [selectedSegment, setSelectedSegment] = useState('')
  const [selectedLevel, setSelectedLevel] = useState('')

  // Result state
  const [result, setResult] = useState<PermissionCheckResult | null>(null)
  const [loading, setLoading] = useState(false)

  // Server-side user search state
  const [userOptions, setUserOptions] = useState<UserOption[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Build base URL for user search - uses current warehouse context (from "Act as Warehouse" switcher)
  const usersBaseUrl =
    userType === 'warehouse' && warehouseId
      ? `/api/warehouse/${warehouseId}/users/`
      : companyId
        ? `/api/company/${companyId}/user/`
        : null

  // Debounced server-side search for users
  const searchUsers = useCallback(
    async (query: string) => {
      if (!usersBaseUrl) return

      // For warehouse users, search is server-side with ?q= param
      // For company users, we fetch all and filter client-side (endpoint doesn't support search)
      const isWarehouseUser = userType === 'warehouse'

      setUsersLoading(true)
      setSearchError(null)

      try {
        const url = isWarehouseUser && query ? `${usersBaseUrl}?q=${encodeURIComponent(query)}` : usersBaseUrl

        const response = await fetch(url, {
          headers: { 'X-CSRFToken': csrf },
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error('Failed to fetch users')
        }

        const data = await response.json()
        const users = data?.results || data || []

        let mappedUsers = users.map((u: any) => ({
          id: u.id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email,
          email: u.email,
        }))

        // For company users, filter client-side since the endpoint may not support ?q=
        if (!isWarehouseUser && query) {
          const searchLower = query.toLowerCase()
          mappedUsers = mappedUsers.filter(
            (u: UserOption) =>
              u.name.toLowerCase().includes(searchLower) || u.email?.toLowerCase().includes(searchLower)
          )
        }

        setUserOptions(mappedUsers)
      } catch (err: any) {
        setSearchError(err.message || 'Error searching users')
        setUserOptions([])
      } finally {
        setUsersLoading(false)
      }
    },
    [usersBaseUrl, userType, csrf]
  )

  // Handle search input change with debounce
  const handleSearchChange = useCallback(
    (value: string) => {
      setUserSearch(value)

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      // Debounce the search (300ms)
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(value)
      }, 300)
    },
    [searchUsers]
  )

  // Load initial users when dropdown opens (if no users loaded yet)
  useEffect(() => {
    if (userDropdownOpen && userOptions.length === 0 && !usersLoading && !searchError) {
      searchUsers('')
    }
  }, [userDropdownOpen, userOptions.length, usersLoading, searchError, searchUsers])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.searchable-select')) {
        setUserDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Check permission
  const checkPermission = async () => {
    if (!selectedUser || !selectedSegment || !selectedLevel) {
      notify({
        title: 'Missing Fields',
        message: 'Please select a user, segment, and access level.',
        type: 'warning',
      })
      return
    }

    setLoading(true)
    setResult(null)

    try {
      const params = new URLSearchParams({
        user_id: selectedUser.id.toString(),
        segment: selectedSegment,
        level: selectedLevel,
      })

      // Add scope parameters
      if (userType === 'warehouse' && warehouseId) {
        params.set('warehouse_id', warehouseId.toString())
      } else if (companyId) {
        params.set('company_id', companyId.toString())
      }

      const response = await fetch(`/api/rbac/check-permission/?${params.toString()}`, {
        headers: {
          'X-CSRFToken': csrf,
        },
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to check permission')
      }

      const data = await response.json()
      setResult(data)
    } catch (error: any) {
      notify({
        title: 'Error',
        message: error.message || 'Failed to check permission',
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const canCheck = selectedUser && selectedSegment && selectedLevel && !loading

  return (
    <DebuggerContainer>
      <Header>
        <FontAwesomeIcon icon={faBug} />
        <div>
          <h4>Permission Debugger</h4>
          <p>Test if a user has specific permissions. Useful for troubleshooting access issues.</p>
        </div>
      </Header>

      <FormCard>
        <FormRow>
          <FormGroup>
            <label>User</label>
            <SearchableSelect className="searchable-select">
              {selectedUser ? (
                <SelectedValue
                  onClick={() => {
                    setSelectedUser(null)
                    setUserSearch('')
                    setResult(null)
                  }}
                >
                  <span>{selectedUser.name}</span>
                  <button type="button">
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </SelectedValue>
              ) : (
                <>
                  <SearchInput>
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                      type="text"
                      placeholder="Search for a user..."
                      value={userSearch}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onFocus={() => setUserDropdownOpen(true)}
                    />
                  </SearchInput>
                  <DropdownList $show={userDropdownOpen}>
                    {usersLoading ? (
                      <li className="no-results">Searching...</li>
                    ) : searchError ? (
                      <li className="no-results">Error: {searchError}</li>
                    ) : userOptions.length === 0 ? (
                      <li className="no-results">{userSearch ? 'No users found' : 'Type to search for users'}</li>
                    ) : (
                      userOptions.slice(0, 15).map((user) => (
                        <li
                          key={user.id}
                          onClick={() => {
                            setSelectedUser(user)
                            setUserSearch('')
                            setUserDropdownOpen(false)
                            setResult(null)
                          }}
                        >
                          {user.name} {user.email && <small>({user.email})</small>}
                        </li>
                      ))
                    )}
                  </DropdownList>
                </>
              )}
            </SearchableSelect>
          </FormGroup>

          <FormGroup>
            <label>Business Segment</label>
            <Form.Select
              value={selectedSegment}
              onChange={(e) => {
                setSelectedSegment(e.target.value)
                setResult(null)
              }}
            >
              <option value="">Select segment...</option>
              {SEGMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </FormGroup>

          <FormGroup>
            <label>Access Level</label>
            <Form.Select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value)
                setResult(null)
              }}
            >
              <option value="">Select level...</option>
              {LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </FormGroup>
        </FormRow>

        <CheckButton onClick={checkPermission} disabled={!canCheck}>
          <FontAwesomeIcon icon={faShieldAlt} />
          {loading ? 'Checking...' : 'Check Permission'}
        </CheckButton>
      </FormCard>

      {result && (
        <ResultCard $allowed={result.allowed}>
          <ResultHeader $allowed={result.allowed}>
            <FontAwesomeIcon icon={result.allowed ? faCheckCircle : faTimesCircle} />
            <h5>{result.allowed ? 'Permission Granted' : 'Permission Denied'}</h5>
          </ResultHeader>
          <ResultBody>
            <ReasonBox>{result.reason}</ReasonBox>

            <DetailsGrid>
              <DetailItem>
                <label>User Type</label>
                <span style={{ textTransform: 'capitalize' }}>{result.user_type}</span>
              </DetailItem>
              <DetailItem>
                <label>User Role</label>
                <span>{result.user_role}</span>
              </DetailItem>
              <DetailItem>
                <label>Segment</label>
                <span style={{ textTransform: 'capitalize' }}>{result.segment}</span>
              </DetailItem>
              <DetailItem>
                <label>Requested Level</label>
                <span style={{ textTransform: 'capitalize' }}>{result.requested_level}</span>
              </DetailItem>
              <DetailItem>
                <label>Effective Level</label>
                <span style={{ textTransform: 'capitalize' }}>{result.effective_level}</span>
              </DetailItem>
              <DetailItem>
                <label>Override Active</label>
                {result.has_override ? (
                  <OverrideBadge>
                    <FontAwesomeIcon icon={faUserShield} />
                    Yes
                  </OverrideBadge>
                ) : (
                  <span>No</span>
                )}
              </DetailItem>
            </DetailsGrid>

            {result.has_override && (
              <InfoBox>
                <FontAwesomeIcon icon={faInfoCircle} />
                <div>
                  This user has a permission override that modifies their default role access for this segment.
                  Overrides can grant or restrict access beyond what the role normally provides.
                </div>
              </InfoBox>
            )}
          </ResultBody>
        </ResultCard>
      )}
    </DebuggerContainer>
  )
}
